import {
  createApi,
  fetchBaseQuery,
  FetchBaseQueryError,
} from "@reduxjs/toolkit/dist/query/react";

import * as devConfig from "../app/configs/devConfig";
import { getAuthHeader } from "../app/globalState/GlobalAuthState";
import {
  SeasonalInfo,
  SeatPreview,
  SubscriptionPlan,
  SubscriptionStatus,
} from "../features/subscription/subscription.utils";

/** A sentence for a failed billing call (the server answers plain text). */
const billingError = (res: FetchBaseQueryError): string =>
  typeof res.data === "string" && res.data.trim() && res.data.length < 300
    ? res.data
    : "Something went wrong. Please try again.";

/**
 * Per-user (seat) billing of the firm itself (backend api/subscription).
 * NOT the firm's own client invoices (those are BillingSlice / "View Billings").
 */
export const subscriptionSlice = createApi({
  reducerPath: "subscriptionSlice",
  tagTypes: ["Subscription"],
  baseQuery: fetchBaseQuery({
    baseUrl: `${devConfig.getServerBaseUrlForEnv()}`,
    prepareHeaders: (headers) => {
      const authHeader = getAuthHeader();
      headers.set("Authorization", authHeader.Authorization);
      return headers;
    },
  }),
  endpoints: (build) => ({
    getSubscriptionStatus: build.query<SubscriptionStatus, { orgId: string }>({
      query: ({ orgId }) => ({
        url: "api/subscription/status",
        params: { orgId },
      }),
      providesTags: ["Subscription"],
    }),
    getSubscriptionPlan: build.query<SubscriptionPlan, { orgId: string }>({
      query: ({ orgId }) => ({
        url: "api/subscription/plan",
        params: { orgId },
      }),
      providesTags: ["Subscription"],
      transformErrorResponse: billingError,
    }),
    getSeatPreview: build.query<SeatPreview, { orgId: string }>({
      query: ({ orgId }) => ({
        url: "api/subscription/seat-preview",
        params: { orgId },
      }),
      providesTags: ["Subscription"],
    }),
    startCheckout: build.mutation<{ url: string }, { orgId: string }>({
      query: (body) => ({
        url: "api/subscription/checkout",
        method: "POST",
        body,
      }),
      transformErrorResponse: billingError,
    }),
    openBillingPortal: build.mutation<{ url: string }, { orgId: string }>({
      query: (body) => ({
        url: "api/subscription/portal",
        method: "POST",
        body,
      }),
      transformErrorResponse: billingError,
    }),
    completeCheckout: build.mutation<
      { done: boolean },
      { orgId: string; sessionId: string }
    >({
      query: (body) => ({
        url: "api/subscription/checkout-complete",
        method: "POST",
        body,
      }),
      invalidatesTags: ["Subscription"],
      transformErrorResponse: billingError,
    }),
    getSeasonal: build.query<SeasonalInfo, { orgId: string }>({
      query: ({ orgId }) => ({
        url: "api/subscription/seasonal",
        params: { orgId },
      }),
      providesTags: ["Subscription"],
    }),
    setSeasonal: build.mutation<
      SeasonalInfo,
      { orgId: string; userId: string; seasonal: boolean }
    >({
      query: (body) => ({
        url: "api/subscription/seasonal",
        method: "PUT",
        body,
      }),
      invalidatesTags: ["Subscription"],
      transformErrorResponse: billingError,
    }),
    changeInterval: build.mutation<
      SubscriptionPlan,
      { orgId: string; interval: "month" | "year" }
    >({
      query: (body) => ({
        url: "api/subscription/interval",
        method: "PUT",
        body,
      }),
      invalidatesTags: ["Subscription"],
      transformErrorResponse: billingError,
    }),
  }),
});

export const {
  useGetSubscriptionStatusQuery,
  useGetSubscriptionPlanQuery,
  useLazyGetSeatPreviewQuery,
  useStartCheckoutMutation,
  useOpenBillingPortalMutation,
  useCompleteCheckoutMutation,
  useChangeIntervalMutation,
  useGetSeasonalQuery,
  useSetSeasonalMutation,
} = subscriptionSlice;
